import { Bot, ChevronRight, Circle, LoaderCircle, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";

type TaskType = "new" | "optimize";
type RunState = "completed" | "running" | "failed";

interface ListingBrief {
  sku: string;
  productName: string;
  marketplace: string;
  taskType: TaskType;
  brand: string;
  category: string;
  productFacts: string;
  specifications: string;
  targetAudience: string;
  useCases: string;
  painPoints: string;
  differentiators: string;
  competitorAsins: string;
  keywords: string;
  compliance: string;
}

interface ListingOutput {
  itemName: string;
  itemHighlights: string;
  bulletPoints: string[];
  description: string;
  backendKeywords: string;
}

interface ListingRunRecord {
  id: string;
  state: RunState;
  submittedAt: string;
  brief: ListingBrief;
  output?: ListingOutput;
  safeError?: string;
}

const REQUIRED_FIELDS: Array<keyof ListingBrief> = [
  "sku",
  "productName",
  "marketplace",
  "taskType",
  "brand",
  "category",
  "productFacts",
];

const EMPTY_BRIEF: ListingBrief = {
  sku: "",
  productName: "",
  marketplace: "US",
  taskType: "new",
  brand: "",
  category: "",
  productFacts: "",
  specifications: "",
  targetAudience: "",
  useCases: "",
  painPoints: "",
  differentiators: "",
  competitorAsins: "",
  keywords: "",
  compliance: "",
};

const SAMPLE_OUTPUT: ListingOutput = {
  itemName: "NorthPeak Stainless Steel Insulated Water Bottle, 1000ml",
  itemHighlights: "Leakproof vacuum bottle for gym, commute and outdoor hydration",
  bulletPoints: [
    "Double-wall vacuum insulation helps drinks stay at the preferred temperature",
    "Food-grade stainless steel body resists everyday dents and lingering odors",
    "Leakproof screw lid helps protect gym bags, backpacks and work totes",
    "Wide-mouth opening makes filling, pouring and daily cleaning straightforward",
    "1000ml capacity supports workouts, commutes, travel and outdoor activities",
  ],
  description:
    "Built for busy days, this insulated bottle combines dependable temperature control with a durable, easy-clean design.",
  backendKeywords: "vacuum flask gym bottle reusable drink container outdoor hydration",
};

const INITIAL_RECORDS: ListingRunRecord[] = [
  sampleRecord(
    "listing-run-001",
    "completed",
    "SKU-20260815-001",
    "不锈钢真空保温杯 1000ml",
    "US",
    "new",
    "2026-08-15 15:42:18",
    SAMPLE_OUTPUT,
  ),
  sampleRecord(
    "listing-run-002",
    "running",
    "SKU-20260815-002",
    "户外折叠露营铝合金桌",
    "US",
    "new",
    "2026-08-15 15:38:05",
  ),
  sampleRecord(
    "listing-run-003",
    "failed",
    "SKU-20260815-003",
    "硅胶婴儿餐盘吸盘碗",
    "JP",
    "optimize",
    "2026-08-15 14:56:33",
    undefined,
    "产品事实缺少可验证的材质与适用年龄信息，请补充后重新运行。",
  ),
  sampleRecord(
    "listing-run-004",
    "completed",
    "SKU-20260815-004",
    "无线蓝牙耳机 ANC 降噪",
    "US",
    "optimize",
    "2026-08-15 13:21:47",
    SAMPLE_OUTPUT,
  ),
];

export function ListingCopywritingPage() {
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [invalidFields, setInvalidFields] = useState<Set<keyof ListingBrief>>(new Set());
  const [records, setRecords] = useState(INITIAL_RECORDS);
  const [selectedRecord, setSelectedRecord] = useState<ListingRunRecord | null>(null);
  const [notice, setNotice] = useState("");
  const completedRequired = useMemo(
    () => REQUIRED_FIELDS.filter((field) => String(brief[field]).trim()).length,
    [brief],
  );

  function updateField<K extends keyof ListingBrief>(field: K, value: ListingBrief[K]) {
    setBrief((current) => ({ ...current, [field]: value }));
    setInvalidFields((current) => {
      const next = new Set(current);
      next.delete(field);
      return next;
    });
    setNotice("");
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const missing = REQUIRED_FIELDS.filter((field) => !String(brief[field]).trim());
    if (missing.length) {
      setInvalidFields(new Set(missing));
      setNotice(`请补充 ${missing.length} 个必填项后再开始撰写。`);
      document.getElementById(`listing-${missing[0]}`)?.focus();
      return;
    }
    const record: ListingRunRecord = {
      id: crypto.randomUUID(),
      state: "running",
      submittedAt: new Intl.DateTimeFormat("zh-CN", {
        dateStyle: "short",
        timeStyle: "medium",
        hour12: false,
      }).format(new Date()),
      brief: { ...brief },
    };
    setRecords((current) => [record, ...current]);
    setNotice("任务已创建，CrewAI 正在处理产品 Brief。");
  }

  function clearBrief() {
    setBrief(EMPTY_BRIEF);
    setInvalidFields(new Set());
    setNotice("");
  }

  return (
    <div className="listing-workbench-page">
      <ListingPageHeader />
      <form className="listing-brief-panel" noValidate onSubmit={submit}>
        <div className="listing-brief-grid">
          <BriefField
            field="sku"
            invalid={invalidFields.has("sku")}
            label="产品 SKU"
            onChange={updateField}
            placeholder="请输入产品 SKU"
            required
            value={brief.sku}
          />
          <BriefField
            field="productName"
            invalid={invalidFields.has("productName")}
            label="产品品名"
            onChange={updateField}
            placeholder="请输入产品品名"
            required
            value={brief.productName}
          />
          <label className="listing-field">
            <FieldLabel label="目标 Amazon 站点" required />
            <select
              id="listing-marketplace"
              aria-label="目标 Amazon 站点"
              aria-invalid={invalidFields.has("marketplace")}
              aria-required="true"
              onChange={(event) => updateField("marketplace", event.target.value)}
              value={brief.marketplace}
            >
              <option value="US">美国站 Amazon.com</option>
              <option value="JP">日本站 Amazon.co.jp</option>
              <option value="UK">英国站 Amazon.co.uk</option>
              <option value="DE">德国站 Amazon.de</option>
              <option value="CA">加拿大站 Amazon.ca</option>
            </select>
          </label>
          <label className="listing-field">
            <FieldLabel label="任务类型" required />
            <select
              id="listing-taskType"
              aria-label="任务类型"
              aria-invalid={invalidFields.has("taskType")}
              aria-required="true"
              onChange={(event) => updateField("taskType", event.target.value as TaskType)}
              value={brief.taskType}
            >
              <option value="new">新品 Listing</option>
              <option value="optimize">老 Listing 优化</option>
            </select>
          </label>
          <BriefField
            field="brand"
            invalid={invalidFields.has("brand")}
            label="品牌名"
            onChange={updateField}
            placeholder="请输入品牌名"
            required
            value={brief.brand}
          />
          <BriefField
            field="category"
            invalid={invalidFields.has("category")}
            label="类目"
            onChange={updateField}
            placeholder="请输入类目"
            required
            value={brief.category}
          />
          <BriefField
            className="span-3"
            field="productFacts"
            invalid={invalidFields.has("productFacts")}
            label="产品事实（核心规格 / 关键特性 / 功能 / 包含物等）"
            multiline
            onChange={updateField}
            placeholder="请填写产品的核心事实信息，建议包含关键规格、功能、材质、包装清单等要点。"
            required
            value={brief.productFacts}
          />
          <BriefField
            className="span-3 compact"
            field="specifications"
            label="材质 / 尺寸 / 容量 / 颜色 / 数量 / 包装"
            onChange={updateField}
            placeholder="如：不锈钢 / 20cm / 1L / 黑色 / 1件装 / 礼盒包装"
            value={brief.specifications}
          />
          <BriefField
            field="targetAudience"
            label="目标人群"
            onChange={updateField}
            placeholder="如：健身人群 / 家庭用户"
            value={brief.targetAudience}
          />
          <BriefField
            field="useCases"
            label="使用场景"
            onChange={updateField}
            placeholder="如：露营 / 通勤 / 厨房使用"
            value={brief.useCases}
          />
          <BriefField
            field="painPoints"
            label="买家痛点"
            onChange={updateField}
            placeholder="如：易开裂 / 清洁困难"
            value={brief.painPoints}
          />
          <BriefField
            field="differentiators"
            label="差异化卖点"
            multiline
            onChange={updateField}
            placeholder="如：加厚材质 / 真空保温 / 便携设计"
            value={brief.differentiators}
          />
          <BriefField
            field="competitorAsins"
            label="竞品 ASIN"
            multiline
            onChange={updateField}
            placeholder="输入 ASIN，以逗号或回车分隔"
            value={brief.competitorAsins}
          />
          <BriefField
            field="keywords"
            label="已有关键词"
            multiline
            onChange={updateField}
            placeholder="输入关键词，以逗号或回车分隔"
            value={brief.keywords}
          />
          <BriefField
            className="span-3 compact"
            field="compliance"
            label="禁用词与合规限制"
            onChange={updateField}
            placeholder="如：禁止使用的词汇、平台政策限制、合规要求等"
            value={brief.compliance}
          />
        </div>
        <div className="listing-form-actions">
          <span
            className={notice ? "listing-form-notice active" : "listing-form-notice"}
            role="status"
          >
            {notice || `必填项 ${completedRequired} / ${REQUIRED_FIELDS.length}`}
          </span>
          <button className="listing-clear-button" onClick={clearBrief} type="button">
            清空
          </button>
          <button className="listing-submit-button" type="submit">
            开始撰写
          </button>
        </div>
      </form>
      <HistoryTable records={records} onSelect={setSelectedRecord} />
      {selectedRecord && (
        <HistoryDetailDialog record={selectedRecord} onClose={() => setSelectedRecord(null)} />
      )}
    </div>
  );
}

function ListingPageHeader() {
  return (
    <header className="listing-page-header">
      <div>
        <h1>运营 Listing 文案撰写</h1>
        <p>基于 Brief 矩阵结构，沉淀产品卖点，生成符合 Amazon 站点要求的 Listing 文案。</p>
      </div>
      <span className="workflow-version-badge">
        <Bot aria-hidden="true" size={16} />
        固定 CrewAI Workflow v1
      </span>
    </header>
  );
}

function BriefField({
  field,
  label,
  value,
  placeholder,
  onChange,
  required = false,
  invalid = false,
  multiline = false,
  className = "",
}: {
  field: keyof ListingBrief;
  label: string;
  value: string;
  placeholder: string;
  onChange: <K extends keyof ListingBrief>(field: K, value: ListingBrief[K]) => void;
  required?: boolean;
  invalid?: boolean;
  multiline?: boolean;
  className?: string;
}) {
  const shared = {
    id: `listing-${field}`,
    "aria-label": label,
    "aria-invalid": invalid,
    "aria-required": required,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(field, event.target.value),
    placeholder,
    value,
  };
  return (
    <label className={`listing-field ${className}`.trim()}>
      <FieldLabel label={label} required={required} />
      {multiline ? <textarea {...shared} rows={2} /> : <input {...shared} />}
    </label>
  );
}

function FieldLabel({ label, required = false }: { label: string; required?: boolean }) {
  return (
    <span>
      {required && <b aria-hidden="true">*</b>}
      {label}
    </span>
  );
}

function HistoryTable({
  records,
  onSelect,
}: {
  records: ListingRunRecord[];
  onSelect: (record: ListingRunRecord) => void;
}) {
  return (
    <section className="listing-history" aria-labelledby="listing-history-title">
      <h2 id="listing-history-title">历史执行记录</h2>
      <div className="listing-history-table-wrap">
        <table>
          <thead>
            <tr>
              <th>状态</th>
              <th>产品 SKU / 品名</th>
              <th>站点</th>
              <th>任务类型</th>
              <th>提交时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id}>
                <td>
                  <RunStatus state={record.state} />
                </td>
                <td>
                  <strong>{record.brief.sku}</strong>
                  <span>{record.brief.productName}</span>
                </td>
                <td>{marketplaceLabel(record.brief.marketplace)}</td>
                <td>{taskTypeLabel(record.brief.taskType)}</td>
                <td>{record.submittedAt}</td>
                <td>
                  <button onClick={() => onSelect(record)} type="button">
                    查看详情 <ChevronRight aria-hidden="true" size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RunStatus({ state }: { state: RunState }) {
  return (
    <span className={`listing-run-status ${state}`}>
      <Circle aria-hidden="true" fill="currentColor" size={7} />
      {{ completed: "已完成", running: "运行中", failed: "失败" }[state]}
    </span>
  );
}

function HistoryDetailDialog({
  record,
  onClose,
}: {
  record: ListingRunRecord;
  onClose: () => void;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  return (
    <div className="listing-detail-backdrop" role="presentation">
      <section
        aria-labelledby="listing-detail-title"
        aria-modal="true"
        className="listing-detail-dialog"
        role="dialog"
      >
        <header>
          <div>
            <RunStatus state={record.state} />
            <h2 id="listing-detail-title">{record.brief.productName}</h2>
            <small>
              {record.brief.sku} · {record.submittedAt}
            </small>
          </div>
          <button aria-label="关闭运行详情" onClick={onClose} ref={closeButton} type="button">
            <X aria-hidden="true" size={20} />
          </button>
        </header>
        <div className="listing-detail-content">
          <section>
            <h3>本次输入</h3>
            <BriefSummary brief={record.brief} />
          </section>
          <section>
            <h3>运行输出</h3>
            <RunOutput record={record} />
          </section>
        </div>
      </section>
    </div>
  );
}

function BriefSummary({ brief }: { brief: ListingBrief }) {
  const entries = [
    ["目标站点", marketplaceLabel(brief.marketplace)],
    ["任务类型", taskTypeLabel(brief.taskType)],
    ["品牌名", brief.brand],
    ["类目", brief.category],
    ["产品事实", brief.productFacts],
    ["材质与规格", brief.specifications],
    ["目标人群", brief.targetAudience],
    ["使用场景", brief.useCases],
    ["买家痛点", brief.painPoints],
    ["差异化卖点", brief.differentiators],
    ["竞品 ASIN", brief.competitorAsins],
    ["已有关键词", brief.keywords],
    ["合规限制", brief.compliance],
  ];
  return (
    <dl className="listing-brief-summary">
      {entries.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value || "未填写"}</dd>
        </div>
      ))}
    </dl>
  );
}

function RunOutput({ record }: { record: ListingRunRecord }) {
  if (record.state === "running")
    return (
      <div className="listing-output-state">
        <LoaderCircle aria-hidden="true" className="listing-running-spinner" size={24} />
        <strong>关键词分析师正在读取市场数据</strong>
        <p>完成后将在这里显示 Listing 文案。</p>
      </div>
    );
  if (record.state === "failed")
    return (
      <div className="alert error" role="alert">
        {record.safeError}
      </div>
    );
  const output = record.output!;
  return (
    <div className="listing-output">
      <label>Item Name</label>
      <p>{output.itemName}</p>
      <label>Item Highlights</label>
      <p>{output.itemHighlights}</p>
      <label>Bullet Points</label>
      <ol>
        {output.bulletPoints.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
      <label>Product Description</label>
      <p>{output.description}</p>
      <label>Backend Search Terms</label>
      <p>{output.backendKeywords}</p>
    </div>
  );
}

function sampleRecord(
  id: string,
  state: RunState,
  sku: string,
  productName: string,
  marketplace: string,
  taskType: TaskType,
  submittedAt: string,
  output?: ListingOutput,
  safeError?: string,
): ListingRunRecord {
  return {
    id,
    state,
    submittedAt,
    output,
    safeError,
    brief: {
      ...EMPTY_BRIEF,
      sku,
      productName,
      marketplace,
      taskType,
      brand: "NorthPeak",
      category: "Home & Kitchen",
      productFacts: "经用户确认的产品规格、材质、功能和包装内容。",
      specifications: "不锈钢 / 1000ml / 黑色 / 1 件装",
      targetAudience: "通勤与户外人群",
      useCases: "健身、通勤、露营",
      painPoints: "普通水杯易漏、保温时间短",
      differentiators: "双层真空保温与防漏杯盖",
      competitorAsins: "B09XYZ1234, B08ABC5678",
      keywords: "保温杯, 不锈钢水杯, 户外水壶",
      compliance: "不得使用 best、#1 等无证据夸张词",
    },
  };
}

function marketplaceLabel(value: string): string {
  return (
    {
      US: "美国站 Amazon.com",
      JP: "日本站 Amazon.co.jp",
      UK: "英国站 Amazon.co.uk",
      DE: "德国站 Amazon.de",
      CA: "加拿大站 Amazon.ca",
    }[value] ?? value
  );
}

function taskTypeLabel(value: TaskType): string {
  return value === "new" ? "新品 Listing" : "老 Listing 优化";
}
